import { Calendar, Lock, Target } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { toast } from '@/shared/lib/toast'

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
]

function AssignTargetContent({ sales, onAssignTarget, onOpenChange }) {
  const now = new Date()
  const currentMonth = now.getMonth() + 1
  const currentYear = now.getFullYear()

  const currentMonthObj = MONTHS.find((m) => m.value === currentMonth)

  const existing = (sales?.monthlyTargets || []).find(
    (t) => Number(t.month) === currentMonth && Number(t.year) === currentYear,
  )

  const [targetValue, setTargetValue] = useState(
    existing ? String(existing.target) : '',
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  const numericTarget = Number(targetValue)

  const handleSubmit = async (e) => {
    e?.preventDefault()
    if (!sales?._id) return

    if (isNaN(numericTarget) || numericTarget < 0) {
      toast.error('Please enter a valid non-negative target value')
      return
    }

    setIsSubmitting(true)
    try {
      await onAssignTarget(sales._id, {
        month: currentMonth,
        year: currentYear,
        target: numericTarget,
      })
      toast.success(
        `Target for ${currentMonthObj?.label} ${currentYear} assigned successfully!`,
      )
      onOpenChange(false)
    } catch (err) {
      console.error(err)
      toast.error(err?.message || 'Failed to assign target')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md max-h-[90vh] overflow-y-auto rounded-2xl sm:rounded-3xl p-4 sm:p-7 shadow-2xl">
      <DialogHeader className="space-y-1">
        <div className="flex items-center gap-2 text-[#0F172A]">
          <Target className="h-5 w-5" />
          <span className="text-xs font-bold uppercase tracking-wider">
            Sales Target Management
          </span>
        </div>
        <DialogTitle className="text-lg sm:text-xl font-bold tracking-tight text-slate-900">
          Assign Monthly Target
        </DialogTitle>
        <DialogDescription className="text-xs text-slate-500">
          Assign or update target for{' '}
          <span className="font-semibold text-slate-700">{sales?.name}</span>.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        {/* Current Month Lock Notice */}
        <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 p-3.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-indigo-600" />
              <span className="text-xs font-bold text-indigo-950">
                {currentMonthObj?.label} {currentYear}
              </span>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-200/70 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-indigo-800">
              <Lock className="h-2.5 w-2.5" />
              Current Month
            </span>
          </div>
          <p className="mt-1 text-[11px] text-indigo-700/80">
            Targets can only be assigned for the active current month. Previous
            and upcoming months are locked.
          </p>
        </div>

        {/* Target Value Input */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label
              htmlFor="targetValue"
              className="text-xs font-semibold text-slate-700"
            >
              Target Value (₹) for {currentMonthObj?.label}
            </Label>
          </div>

          <div>
            <Input
              id="targetValue"
              type="number"
              min="0"
              step="any"
              placeholder="Enter target value (₹)... e.g. 500000"
              value={targetValue}
              onChange={(e) => setTargetValue(e.target.value)}
              className="h-11 rounded-xl text-sm font-semibold border-slate-200 text-slate-900 focus:border-indigo-500 focus:ring-indigo-500/20"
              autoFocus
            />
          </div>

          <p className="text-[11px] text-slate-400">
            Enter the sales/revenue target value in Rupees for this salesperson.
          </p>
        </div>

        <DialogFooter className="mt-6 flex flex-col-reverse sm:flex-row items-center justify-end gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            className="w-full sm:w-auto h-10 rounded-xl px-4 text-xs font-semibold"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || targetValue === ''}
            className="w-full sm:w-auto h-10 rounded-xl bg-[#0F172A] hover:bg-[#0F172A] text-white px-5 text-xs font-semibold shadow-xs"
          >
            {isSubmitting ? 'Saving...' : 'Save Target'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}

export function AssignTargetDialog({
  open,
  onOpenChange,
  sales,
  onAssignTarget,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <AssignTargetContent
          sales={sales}
          onAssignTarget={onAssignTarget}
          onOpenChange={onOpenChange}
        />
      )}
    </Dialog>
  )
}
